import { groupDetailOptions, groupsListOptions } from '@doneverse/data/queries'
import type { GroupListItem } from '@doneverse/shared'
import { useQuery } from '@tanstack/react-query'
import { Outlet, useParams, useRouterState } from '@tanstack/react-router'
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { ManageGroupDialog } from '@/components/groups/GroupAdminDialogs'
import type { GroupsInboxView } from '@/components/groups/GroupsInboxLinks'
import { GroupsSidebar } from '@/components/groups/GroupsSidebar'
import { PageLayout } from '@/components/layout/PageLayout'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/contexts/AuthContext'

const EMPTY_GROUPS: GroupListItem[] = []

interface GroupsLayoutCtx {
  /** Open the spaces sidebar as a sheet (mobile only — no-op on desktop). */
  openSpaces: () => void
}

const GroupsLayoutContext = createContext<GroupsLayoutCtx | null>(null)

export function useGroupsLayout(): GroupsLayoutCtx {
  const ctx = useContext(GroupsLayoutContext)
  if (!ctx) throw new Error('useGroupsLayout must be used inside GroupsLayout')
  return ctx
}

const INBOX_HEADER: Record<GroupsInboxView, { title: string; subtitle: ReactNode }> = {
  mentions: {
    title: 'Mentions',
    subtitle: (
      <>
        Posts and comments that tag you directly, plus <code>@all</code> broadcasts from groups you belong to.
      </>
    ),
  },
  replies: {
    title: 'Replies',
    subtitle: 'Comments on your posts and direct replies to your comments.',
  },
}

function viewFromPath(pathname: string): GroupsInboxView | null {
  if (pathname.endsWith('/groups/mentions')) return 'mentions'
  if (pathname.endsWith('/groups/replies')) return 'replies'
  return null
}

/**
 * Shared shell for every `/groups*` route — for founders, doers, and admins
 * alike. Owns the persistent left rail and the column grid; the top bar comes
 * from PageLayout so global affordances (feedback, notifications) stay put.
 * Admins see every group (scope=all) and get inline manage / create
 * affordances; there is no separate admin groups page.
 *
 * Mobile: the rail collapses into a Sheet opened via `useGroupsLayout().openSpaces`
 * — each page renders its own trigger so the affordance lives near content.
 */
export function GroupsLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const { groupId } = useParams({ strict: false }) as { groupId?: string }
  const { profile } = useAuth()
  const isAdmin = profile?.role === 'admin'
  const view = viewFromPath(pathname)
  const [spacesOpen, setSpacesOpen] = useState(false)

  // Admins browse all groups; everyone else sees their memberships + discover.
  const all = useQuery({ ...groupsListOptions('all'), enabled: isAdmin })
  const mine = useQuery({ ...groupsListOptions('mine'), enabled: !isAdmin })
  const discover = useQuery({ ...groupsListOptions('discover'), enabled: !isAdmin })

  const sidebarGroups = (isAdmin ? all.data : mine.data) ?? EMPTY_GROUPS
  const discoverGroups = (isAdmin ? undefined : discover.data) ?? EMPTY_GROUPS
  const sidebarLoading = isAdmin ? all.isLoading : mine.isLoading

  const detail = useQuery(groupDetailOptions(groupId))
  const d = detail.data
  const canManage = !!d && (isAdmin || d.viewer_role === 'owner' || d.viewer_role === 'moderator')

  // Dismiss the mobile sheet whenever the route changes — navigating to a
  // different group / inbox view should reveal the content underneath.
  useEffect(() => {
    setSpacesOpen(false)
  }, [pathname])

  let title: ReactNode
  let subtitle: ReactNode = null
  if (groupId) {
    title = d ? d.name : <Skeleton className="h-7 w-72 rounded" />
    subtitle = d ? (d.description ?? null) : <Skeleton className="h-3 w-96 rounded" />
  } else if (view) {
    title = INBOX_HEADER[view].title
    subtitle = INBOX_HEADER[view].subtitle
  } else {
    title = 'Community'
  }

  const sidebar = (
    <GroupsSidebar
      isAdmin={isAdmin}
      activeView={view}
      activeGroupId={groupId ?? null}
      myGroups={sidebarGroups}
      discoverGroups={discoverGroups}
      isLoading={sidebarLoading}
    />
  )

  return (
    <PageLayout
      title={title}
      subtitle={subtitle}
      actions={groupId && canManage ? <ManageGroupDialog groupId={groupId} /> : null}
    >
      <GroupsLayoutContext.Provider value={{ openSpaces: () => setSpacesOpen(true) }}>
        {/* Page scrolls inside the global ScrollArea so the auto-hide header
            works; the left rail (and GroupDetail's right rail) stick below
            the fixed header on desktop, scrolling only if their own content
            overflows the viewport. */}
        <div className="grid min-w-0 gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
          <aside className="hidden self-start overflow-y-auto rounded-2xl border border-surface-border bg-background lg:sticky lg:top-[var(--page-header-height,0px)] lg:block lg:max-h-[calc(100dvh-var(--page-header-height,0px))]">
            {sidebar}
          </aside>
          <div className="min-w-0">
            <Outlet />
          </div>
        </div>

        <Sheet open={spacesOpen} onOpenChange={setSpacesOpen}>
          <SheetContent
            side="left"
            aria-describedby={undefined}
            className="w-[300px] overflow-y-auto bg-background p-0 pt-[var(--safe-area-inset-top)] pb-[var(--safe-area-inset-bottom)] lg:hidden"
          >
            <SheetTitle className="sr-only">Spaces</SheetTitle>
            {sidebar}
          </SheetContent>
        </Sheet>
      </GroupsLayoutContext.Provider>
    </PageLayout>
  )
}
